import { useTranslations } from '@vocab/react';
import {
  Box,
  Tab,
  Tabs,
  TabsProvider,
  useResponsiveValue,
} from 'braid-design-system';
import {
  type FC,
  useEffect,
  type Dispatch,
  type SetStateAction,
  useRef,
} from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import {
  trackClickCandidateListTab,
  trackClickHirerActivityListTab,
  trackClickRecommendedCandidatesTab,
} from 'src/utils/events';
import { navigateToPage } from 'src/utils/window';

import translations from './.vocab';

import * as styles from './PageTabs.css';

export type PageTab = 'applications' | 'recommended-candidates' | 'notes';

interface PageTabsProps {
  jobId: number;
  showRecommendedCandidates: boolean;
  onApplicationsTabClick: () => void;
  selectedPageTab: PageTab;
  setSelectedPageTab: (tab: PageTab) => void;
  isStickyOnDesktop: boolean;
  setIsStickyOnDesktop: Dispatch<SetStateAction<boolean>>;
}

export const PageTabs: FC<PageTabsProps> = ({
  jobId,
  showRecommendedCandidates,
  isStickyOnDesktop,
  selectedPageTab,
  setIsStickyOnDesktop,
  setSelectedPageTab,
  onApplicationsTabClick,
}) => {
  const { t } = useTranslations(translations);

  const hideNotesTab = useResponsiveValue()({ mobile: true, tablet: false });

  const { router } = useAppConfig();

  // https://stackoverflow.com/questions/57088861/react-setstate-hook-from-scroll-event-listener
  const prevScrollY = useRef(0);
  useEffect(() => {
    const handleScroll = () => {
      // This -12 offsets the PageTabs from flicking in and out of stickiness when scrolling up or down by a small amount
      const currentScrollY = isStickyOnDesktop
        ? window.scrollY - 12 // FIXME: NVL - update this offset value
        : window.scrollY;
      if (prevScrollY.current < currentScrollY && isStickyOnDesktop) {
        setIsStickyOnDesktop(false);
      } else if (prevScrollY.current > currentScrollY && !isStickyOnDesktop) {
        setIsStickyOnDesktop(true);
      }

      prevScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isStickyOnDesktop, setIsStickyOnDesktop]);

  useEffect(() => {
    if (hideNotesTab && selectedPageTab === 'notes') {
      setSelectedPageTab('applications');
    }
  }, [hideNotesTab, selectedPageTab, setSelectedPageTab]);

  return (
    <TabsProvider
      id="page-tabs"
      selectedItem={selectedPageTab}
      onChange={(_, pageTab) => {
        if (selectedPageTab === pageTab) {
          return;
        }
        switch (pageTab as PageTab) {
          case 'applications':
            trackClickCandidateListTab();
            onApplicationsTabClick();
            break;
          case 'recommended-candidates':
            trackClickRecommendedCandidatesTab();
            navigateToPage(
              router.recommendedCandidates({
                jobId,
              }),
            );
            break;
          case 'notes':
            trackClickHirerActivityListTab();
            setSelectedPageTab('notes');
        }
        setSelectedPageTab(pageTab as PageTab);
      }}
    >
      <Box
        className={{
          [styles.stickyTabs]: isStickyOnDesktop,
        }}
        background="surface"
        paddingLeft={['small', 'large']}
        width="full"
        boxShadow="small"
        paddingTop="small"
      >
        <Tabs label="Page tabs" divider="none" size="small">
          <Tab item="applications">{t('Applications')}</Tab>
          {showRecommendedCandidates && (
            <Tab item="recommended-candidates">{t('Candidate Matches')}</Tab>
          )}
          {!hideNotesTab && (
            <Tab item="notes">
              <Box data={{ intercomid: 'notesTab' }}>{t('Notes')}</Box>
            </Tab>
          )}
        </Tabs>
      </Box>
    </TabsProvider>
  );
};
